import { useQuery } from '@tanstack/react-query';
import { loadStaticData } from '@/lib/offlineData';

/**
 * Static per-hospital JSON structure from generate_hospital_detail_cache.py.
 * Compact field names to minimize file size.
 */

export interface StaticHospitalMeta {
  id: string;
  name: string;
  address: string | null;
  city: string;
  state: string;
  zip_code: string | null;
  npi_number: string | null;
  phone_number: string | null;
  website_url: string | null;
  chargemaster_url: string | null;
  latitude: number | null;
  longitude: number | null;
  bed_count: number | null;
  hospital_type: string | null;
  ownership: string | null;
  google_rating: number | null;
  google_review_count: number | null;
  google_maps_url: string | null;
  cms_overall_rating: number | null;
  cms_mortality_rating: string | null;
  cms_readmission_rating: string | null;
  cms_patient_experience_rating: string | null;
  cms_safety_rating: string | null;
  emergency_services: boolean | null;
  data_quality_score: number | null;
}

export interface StaticRankingSummary {
  total_procedures: number;
  avg_gross_markup_percentage: number | null;
  // Median-baseline markup — the hospital-level headline metric that
  // overall_rank_gross is ordered by.
  median_gross_markup_percentage?: number | null;
  worst_markup_percentage: number | null;
  best_markup_percentage: number | null;
  expensive_procedures_count: number;
  cheap_procedures_count: number;
  outlier_expensive_count: number;
  outlier_cheap_count: number;
  overall_rank_gross: number | null;
  shame_score_calculated: number | null;
  outlier_percentage: number | null;
  rank_position: number | null;
  cpt_procedure_count: number;
  cpt_avg_markup_percentage: number | null;
  cpt_total_available: number;
  drg_procedure_count: number;
  drg_avg_markup_percentage: number | null;
  drg_total_available: number;
  rev_procedure_count: number;
  rev_avg_markup_percentage: number | null;
  rev_total_available: number;
  ndc_procedure_count: number;
  ndc_avg_markup_percentage: number | null;
  ndc_total_available: number;
  hcpcs_procedure_count: number;
  hcpcs_avg_markup_percentage: number | null;
  hcpcs_total_available: number;
  supply_procedure_count: number;
  supply_avg_markup_percentage: number | null;
  supply_total_available: number;
  drug_procedure_count: number;
  drug_avg_markup_percentage: number | null;
  drug_total_available: number;
  uncoded_procedures: number;
  total_available_procedures: number;
  expensive_procedures_percentage: number | null;
}

/** Compact chargemaster mapping from static JSON. */
export interface StaticMapping {
  cc?: string;  // chargemaster_code
  cp?: string;  // cpt_code
  hc?: string;  // hcpcs_code
  dr?: string;  // drg_code
  rv?: string;  // revenue_code
  nd?: string;  // ndc_code
  su?: string;  // supply_code
  du?: string;  // drug_code
  pd?: string;  // procedure_description
  gc?: number;  // gross_charge
  dc?: number;  // discounted_cash_price
  mn?: number;  // min_negotiated_rate
  mx?: number;  // max_negotiated_rate
  ap?: number;  // averaged_price
  io?: string;  // inpatient_outpatient
  nu?: string;  // normalized_unit
  np?: number;  // normalized_price_per_unit
  ps?: string;  // price_source
}

export interface StaticMappingsMeta {
  total_raw: number;  // raw count before dedup
  total: number;      // deduped count (what's in mappings array)
  avg_gross: number | null;
  max_gross: number | null;
}

export interface StaticHospitalData {
  meta?: StaticHospitalMeta;
  ranking?: StaticRankingSummary;
  mappings?: StaticMapping[];
  mappings_meta?: StaticMappingsMeta;
  // Analytics per procedure type (for drilldown modals)
  [key: string]: any;
}

/**
 * Expand a compact StaticMapping into the shape that HospitalMappingsTable expects
 * from chargemaster_mappings Supabase query (SELECT *).
 */
export function expandMapping(m: StaticMapping, index: number) {
  return {
    id: `static-${index}`,
    chargemaster_code: m.cc || null,
    cpt_code: m.cp || null,
    hcpcs_code: m.hc || null,
    drg_code: m.dr || null,
    revenue_code: m.rv || null,
    ndc_code: m.nd || null,
    supply_code: m.su || null,
    drug_code: m.du || null,
    procedure_description: m.pd || null,
    chargemaster_description: m.pd || null,
    gross_charge: m.gc ?? null,
    discounted_cash_price: m.dc ?? null,
    min_negotiated_rate: m.mn ?? null,
    max_negotiated_rate: m.mx ?? null,
    averaged_price: m.ap ?? null,
    inpatient_outpatient: m.io?.toLowerCase() || null,
    normalized_unit: m.nu || null,
    normalized_price_per_unit: m.np ?? null,
    price_source: m.ps || null,
  };
}

/**
 * Hook to load per-hospital static JSON data.
 * Returns the full static file content, or null if not available.
 */
export function useStaticHospitalData(hospitalId: string | undefined) {
  return useQuery({
    queryKey: ['static-hospital-data', hospitalId],
    queryFn: async () => {
      if (!hospitalId) return null;
      const data = await loadStaticData<StaticHospitalData>(`hospitals/${hospitalId}.json`);
      return data || null;
    },
    enabled: !!hospitalId,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 24 * 60 * 60 * 1000,
  });
}
